<template>
  <div class="auth-wrapper auth-v1 px-2">
    <div class="auth-inner register py-2">
      <b-card class="mb-0">
        <b-link class="brand-logo">
          <logo />
        </b-link>

        <b-card-title class="mb-1 text-center">
          {{ t('Sign Up') }}
        </b-card-title>

        <validation-observer ref="registerForm">
          <b-form class="auth-login-form mt-2" @submit.prevent="register">
            <b-row>
              <b-col sm="6">
                <b-form-group label-for="customer_type" :label="t('Customer Type')">
                  <validation-provider
                    #default="{ errors }"
                    ref="customer_type"
                    :name="t('Customer Type')"
                    rules="required"
                  >
                    <b-form-select
                      id="customer_type"
                      v-model="values.type"
                      :options="CustomerTypeOptions"
                      placeholder="t('Customer Type')"
                      :class="[errors[0] && 'is-invalid']"
                      :tabindex="2"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col sm="6">
                <b-form-group>
                  <div class="d-flex form-group-label">
                    <label for="firstname">{{ t('First Name') }}</label>
                    <span class="text-danger">*</span>
                  </div>
                  <validation-provider
                    #default="{ errors }"
                    :name="t('First Name')"
                    rules="required"
                  >
                    <b-form-input
                      id="firstname"
                      v-model="values.firstname"
                      name="firstname"
                      :state="errors.length > 0 ? false : null"
                      :placeholder="t('First Name')"
                      tabindex="1"
                      autofocus
                      autocomplete="off"
                      @keypress="isText($event)"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col sm="6">
                <b-form-group>
                  <div class="d-flex form-group-label">
                    <label for="lastname">{{ t('Last Name') }}</label>
                    <span class="text-danger">*</span>
                  </div>
                  <validation-provider
                    #default="{ errors }"
                    :name="t('Last Name')"
                    rules="required"
                  >
                    <b-form-input
                      id="lastname"
                      v-model="values.lastname"
                      name="lastname"
                      :state="errors.length > 0 ? false : null"
                      :placeholder="t('Last Name')"
                      tabindex="2"
                      autocomplete="off"
                      @keypress="isText($event)"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col sm="6">
                <b-form-group>
                  <div class="d-flex form-group-label">
                    <label for="phone">{{ t('Phone') }}</label>
                    <span class="text-danger">*</span>
                  </div>
                  <validation-provider #default="{ errors }" :name="t('Phone')" rules="required">
                    <b-input-group>
                      <b-input-group-prepend is-text> TR (+90) </b-input-group-prepend>
                      <b-form-input
                        id="phone"
                        v-model="values.phone"
                        name="phone"
                        type="number"
                        :state="errors.length > 0 ? false : null"
                        :placeholder="t('Phone')"
                        tabindex="3"
                        maxlength="10"
                        autocomplete="off"
                      />
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col sm="6">
                <b-form-group>
                  <div class="d-flex form-group-label">
                    <label for="email">{{ t('Email') }}</label>
                    <span class="text-danger">*</span>
                  </div>
                  <validation-provider
                    #default="{ errors }"
                    :name="t('Email')"
                    rules="required|email"
                  >
                    <b-input-group>
                      <b-form-input
                        id="email"
                        v-model="values.email"
                        name="email"
                        :state="errors.length > 0 ? false : null"
                        :placeholder="t('Email')"
                        tabindex="4"
                        autocomplete="off"
                      />
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col sm="6">
                <b-form-group>
                  <div class="d-flex form-group-label">
                    <label for="password">{{ t('Password') }}</label>
                    <span class="text-danger">*</span>
                  </div>
                  <validation-provider
                    #default="{ errors }"
                    :name="t('Password')"
                    rules="required|uppercase_and_digit|min:6"
                  >
                    <b-input-group
                      class="input-group-merge"
                      :class="errors.length > 0 ? 'is-invalid' : null"
                    >
                      <b-form-input
                        id="password"
                        v-model="values.password"
                        :type="passwordFieldType"
                        class="form-control-merge"
                        :state="errors.length > 0 ? false : null"
                        name="password"
                        :placeholder="t('Password')"
                        tabindex="5"
                        autocomplete="off"
                      />

                      <b-input-group-append is-text>
                        <feather-icon
                          class="cursor-pointer"
                          :icon="passwordToggleIcon"
                          @click="togglePasswordVisibility"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col v-if="values.type === 2" sm="6">
                <b-form-group>
                  <div class="d-flex form-group-label">
                    <label for="name">{{ t('Company Name') }}</label>
                    <span class="text-danger">*</span>
                  </div>
                  <validation-provider
                    #default="{ errors }"
                    :name="t('Company Name')"
                    rules="required"
                  >
                    <b-form-input
                      id="name"
                      v-model="company_name"
                      name="name"
                      :state="errors.length > 0 ? false : null"
                      :placeholder="t('Company Name')"
                      tabindex="6"
                      autocomplete="off"
                      @keypress="isText($event)"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row />

            <b-row />

            <b-row v-if="values.type === 2">
              <b-col sm="6">
                <b-form-group>
                  <div class="d-flex form-group-label">
                    <label for="tax_office">{{ t('Tax Office') }}</label>
                    <span class="text-danger">*</span>
                  </div>
                  <validation-provider
                    #default="{ errors }"
                    :name="t('Tax Office')"
                    rules="required"
                  >
                    <b-form-input
                      id="tax_office"
                      v-model="tax_office"
                      name="tax_office"
                      :placeholder="t('Tax Office')"
                      :state="errors.length > 0 ? false : null"
                      tabindex="11"
                      autocomplete="off"
                      @keypress="isText($event)"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col sm="6">
                <b-form-group>
                  <div class="d-flex form-group-label">
                    <label for="tax_number">{{ t('Tax Number') }}</label>
                    <span class="text-danger">*</span>
                  </div>
                  <validation-provider
                    #default="{ errors }"
                    :name="t('Tax Number')"
                    rules="required"
                  >
                    <b-form-input
                      id="tax_no"
                      v-model="tax_no"
                      name="tax_no"
                      type="number"
                      :formatter="formatTaxNumber"
                      :state="errors.length > 0 ? false : null"
                      :placeholder="t('Tax Number')"
                      tabindex="12"
                      autocomplete="off"
                      @keypress="isNumber($event)"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col>
                <b-form-group>
                  <div class="d-flex form-group-label">
                    <label for="Address">{{ t('Address') }}</label>
                    <span class="text-danger">*</span>
                  </div>
                  <validation-provider #default="{ errors }" :name="t('Address')" rules="required">
                    <b-form-textarea
                      id="address"
                      v-model="values.address"
                      :placeholder="t('Address')"
                      rows="2"
                      autocomplete="off"
                      tabindex="13"
                      no-resize
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col>
                <b-form-group label-for="recaptcha" class="mt-2px ml-2px">
                  <validation-provider
                    #default="{ errors }"
                    :name="t('Verification')"
                    rules="required"
                  >
                    <div ref="recaptchaContainer" />
                    <input id="recaptcha" v-model="recaptchaValue" name="recaptcha" type="hidden" />

                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col sm="6">
                <b-button
                  class=""
                  variant="primary"
                  type="submit"
                  tabindex="14"
                  :disabled="submitting"
                  block
                >
                  {{ submitting ? t('Loading') : t('Sign Up') }}
                </b-button>
              </b-col>
              <b-col>
                <div class="w-100 d-flex justify-content-center align-content-center">
                  <b-badge
                    pill
                    variant="dark cursor-pointer signup-login-text"
                    @click="routingToLogin"
                  >
                    {{ t('Sign In') }}
                  </b-badge>
                </div>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card>
    </div>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import {
  BButton,
  BForm,
  BFormInput,
  BFormGroup,
  BCard,
  BLink,
  BCardTitle,
  BInputGroup,
  BInputGroupAppend,
  BCol,
  BInputGroupPrepend,
  BRow,
  BFormTextarea,
  BFormSelect,
  BBadge
} from 'bootstrap-vue';
import Logo from '@core/layouts/components/Logo.vue';
import { required, email, uppercase_and_digit } from '@validations';
import { togglePasswordVisibility } from '@core/mixins/ui/forms';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper';
import useService from '@/auth/service/useService';

export default {
  components: {
    BBadge,
    BRow,
    // BSV
    BButton,
    BForm,
    BFormInput,
    BFormGroup,
    BCard,
    BCardTitle,
    BLink,
    BCol,
    Logo,
    BInputGroup,
    BInputGroupAppend,
    BInputGroupPrepend,
    BFormTextarea,
    BFormSelect,
    ValidationProvider,
    ValidationObserver
  },

  mixins: [togglePasswordVisibility, helper],

  data() {
    return {
      values: {
        type: 1,
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        password: '',
        address: ''
      },
      recaptchaWidgetId: null,
      recaptchaValue: '',
      company_name: '',
      tax_office: '',
      tax_no: '',

      // validation rules
      required,
      email,
      uppercase_and_digit,

      options: {
        phone: {
          phone: true,
          phoneRegionCode: 'TR'
        }
      },

      CustomerTypeOptions: [],
      statusOptions: [],
      submitting: false
    };
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeOffIcon' : 'EyeIcon';
    }
  },

  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  },

  mounted() {
    this.CustomerTypeOptions = [
      { text: this.$t('Make a Choice'), value: null, disabled: true },
      { text: this.$t('Personal'), value: 1 },
      { text: this.$t('Business'), value: 2 }
    ];
    this.recaptchaWidgetId = this.$refs.recaptchaContainer;
    this.initRecaptcha();
  },

  methods: {
    initRecaptcha() {
      if (typeof grecaptcha === 'object') {
        // eslint-disable-next-line no-undef
        grecaptcha.ready(() => {
          // eslint-disable-next-line no-undef
          grecaptcha.render(this.recaptchaWidgetId, {
            sitekey: process.env.VUE_APP_RECAPTCHA,
            callback: this.recaptchaCallback,
            'expired-callback': this.recaptchaExpiredCallback
          });
        });
      }
    },
    recaptchaCallback(token) {
      this.recaptchaValue = token;
    },
    recaptchaExpiredCallback() {
      this.recaptchaValue = '';
    },

    register() {
      this.$refs.registerForm.validate().then(success => {
        if (success) {
          const requestData = {
            ...this.values,
            recaptcha: this.recaptchaValue
          };

          if (this.values.type === 2) {
            requestData.company_name = this.company_name;
            requestData.tax_office = this.tax_office;
            requestData.tax_no = this.tax_no;
          }

          this.submitting = true;

          useService
            .register(requestData)

            .then(response => {
              this.showSuccessMessage(
                `${response.message || this.$t('Registration Successful Message')}`
              );
              this.$router.push('/login');
            })
            .catch(messages => {
              if (typeof messages === 'object' && messages && messages.length) {
                messages.forEach(item => {
                  this.showErrorMessage(item.message);
                });
                this.recaptchaReset();
              }
            })
            .finally(() => {
              this.submitting = false;
            });
        }
      });
    },
    isText(e) {
      const char = String.fromCharCode(e.keyCode);

      if (/^[a-zA-Z_ğüşöçıİĞÜŞÖÇ ]+$/.test(char)) {
        return true;
      }

      e.preventDefault();
      return false;
    },

    isNumber(e) {
      const invalidChars = ['-', '+', 'e', 'E', '+', '^'];
      if (invalidChars.includes(e.key)) {
        e.preventDefault();
      }
    },
    routingToLogin() {
      this.$router.push('/login');
    },

    formatTaxNumber(e) {
      return String(e).substring(0, 10);
    }
  },
  recaptchaReset() {
    if (typeof grecaptcha === 'object') {
      // eslint-disable-next-line no-undef
      grecaptcha.reset(this.recaptchaWidgetId);
      this.recaptchaValue = '';
    }
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';

.signup-login-text {
  margin-top: 8px;
}
</style>
